import { zodResolver } from '@hookform/resolvers/zod';
import { createTransaction } from 'api/transaction';
import Button from 'components/form/Button';
import InputField from 'components/form/Input';
import CopyComponent from 'components/layouts/CopyComponent';
import { NumberDisplay } from 'components/layouts/Display';
import ModalComp from 'components/layouts/Modal';
import { formatNumber } from 'helpers';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import useWalletStore from 'state/wallet';
import { z } from 'zod';

interface ModalProps {
  toggleModal: (value?: boolean) => void;
  plan: {
    profit: number;
    name: string;
    [x: string]: string | number;
  };
  currency: string;
  amount: string;
}

const Schema = z.object({
  transactionId: z.string().min(8, 'too short'),
  imageUrl: z.string().optional(),
});

type SchemaType = z.infer<typeof Schema>;

const ConfirmationModal = ({
  plan,
  currency,
  amount,
  toggleModal,
}: ModalProps) => {
  const { data } = useWalletStore();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = useForm<SchemaType>({
    resolver: zodResolver(Schema),
    mode: 'onTouched',
  });

  const [loading, setLoading] = useState(false);

  const profit = formatNumber(amount) * (plan.profit / 100);

  const makeTransaction = async (data: any) => {
    setLoading(true);
    try {
      const res = await createTransaction(data);

      if (res?.data) {
        reset();
        toast.success('Deposit successful');
        toggleModal(true);
      }
    } catch (error: any) {
      setLoading(false);
      const { errors } = error.response.data;
      errors.map((err: { error: string }) => {
        if (err?.error) {
          return toast.error(err.error);
        } else {
          return toast.error('Something went wrong');
        }
      });
      console.log(error);
    }
  };

  const onSubmit = (data: SchemaType) => {
    makeTransaction({
      transactionId: data.transactionId,
      plan: plan.name,
      crypto: currency,
      amount: formatNumber(amount),
      trxType: 'DEPOSIT',
      // imageUrl: 'https://ershemug.sirv.com/astro/IMG-20220510-WA0009.jpg',
    });
  };

  return (
    <ModalComp toggleModal={toggleModal} width="w-full md:w-[540px]">
      <div className="px-5">
        <h4 className="text-lg">Transaction Breakdown</h4>
        <div className="flex flex-col gap-y-3">
          <ul className="flex flex-col gap-y-1 py-3 ">
            <li className="flex">
              <span className="w-[80%] font-bold">Plan:</span>{' '}
              <span>{plan.name}</span>
            </li>
            <li className="flex">
              <span className="w-[80%] font-bold">Amount:</span>
              <NumberDisplay value={amount} />
            </li>
            <li className="flex">
              <span className="w-[80%] font-bold">Percentage:</span>
              <span>{plan.profit}%</span>
            </li>
            <li className="flex">
              <span className="w-[80%] font-bold">Profit:</span>
              <NumberDisplay value={profit} />
            </li>
            <li className="flex">
              <span className="w-[80%] font-bold">Crypto currency:</span>
              <span className="capitalize">{currency}</span>
            </li>
          </ul>
        </div>
        <hr />
        <div className="flex flex-col gap-y-3 py-3">
          <div className="flex flex-col items-center gap-4 md:flex-row">
            <p>Wallet address</p>
            <CopyComponent
              text={data.find((item) => item.name === currency)?.value || ''}
            />
          </div>
          {/* <UploadImage handleUploadUrl={handleUpload} message="upload proof" /> */}

          {data.find((item) => item.name === currency)?.imageUrl && (
            <div>
              <img
                src={data.find((item) => item.name === currency)?.imageUrl}
                alt="wallet_code"
                className="mx-auto h-[150px] w-[150px]"
              />
            </div>
          )}

          <InputField
            name="transactionId"
            placeholder="transaction id"
            control={control}
          />

          <Button
            className="rounded-md bg-blue-500 px-3 py-2 text-white"
            disabled={!isDirty || !isValid || loading}
            onClick={handleSubmit(onSubmit)}
          >
            complete
          </Button>
        </div>
      </div>
    </ModalComp>
  );
};

export default ConfirmationModal;
